import React, { useCallback, useEffect, useState } from 'react';
import useYupValidationResolver, { CustomError } from 'form/yup/reactHookFormResolver';
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory } from 'react-router';
import { registerPersonalDetails, registerSearchFirms, registerDueDiligence } from 'routes/routePaths';
import Address from 'screens/BrokerPortal/screens/Registration/components/Form/Address/Address';
import Navigation from '../Form/Navigation/Navigation';
import { HorizontalRuler, Section } from '../UI';
import { FIELD_LABELS, FIELD_NAMES, FIRM_TYPE, Form, Props } from './types';
import { CUSTOM_ERROR, validationSchema } from './validationSchema';
import { FindClub, FindDefault, FindNetwork } from './components';
import { Firm } from '../../api';
import { Checkbox, FormInputWrapper, PhoneInput, Search, TextInput } from 'form/infrastructure/reactHookForm';
import RadioGroup from 'form/infrastructure/reactHookForm/RadioGroup';
import { InputError } from 'form/components/InputWrapper/InputWrapper.styled';
import { fetchAddresses, formatAddressLabel } from 'utils/address';
import { ADDRESS_NAMES } from '../Form/Address/types';
import { postcodeValidation } from 'utils/form/validation';
import { PostcodeInput } from 'form/components';
import { Option } from 'form/components/Search/Search';
import { Step } from 'screens/BrokerPortal/screens/Registration/steps';
import canViewRegisterFirmStep from 'screens/BrokerPortal/screens/Registration/guard/canViewRegisterFirmStep';
import { AddressType } from 'components/Forms/Address/types';
import { isNotEmptyString } from 'utils/form/formUtils';
import BoxWrapper from '../Form/RegistrationFormTemplate/BoxWrapper/BoxWrapper';
import { Heading, Text } from 'lmc-ui-library-rebrand';

const RegisterFirmStep: React.FC<Props> = ({
  form,
  searchFirmForm,
  setRegisterFirmForm,
  loadNetworksByNetworkName,
  loadClubsByClubName,
  setContextFirm,
  networks,
  clubs,
  setCurrentStep,
}) => {
  const [addressOptions, setAddressOptions] = useState<any>([]);
  const methods = useForm<Form>({
    mode: 'onBlur',
    resolver: useYupValidationResolver(validationSchema()),
    defaultValues: form,
  });
  const {
    handleSubmit,
    watch,
    trigger,
    getValues,
    setValue,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    formState: { isValid },
  } = methods;
  const history = useHistory();

  const onSubmit = handleSubmit((data) => setRegisterFirmForm(data));

  useEffect(() => {
    if (!canViewRegisterFirmStep(searchFirmForm)) {
      history.push(registerSearchFirms);
    }
  }, [history, searchFirmForm]);

  const handleBack = () => history.push(registerSearchFirms);

  useEffect(() => {
    setCurrentStep(Step.REGISTER_FIRM);
  }, [setCurrentStep]);

  useEffect(() => {
    const notEmptyFields = (Object.keys(getValues()) as [keyof Form]).filter(
      (key) => getValues()[key] !== null && getValues()[key] !== '',
    );
    if (notEmptyFields.length > 0) {
      trigger(notEmptyFields);
    }
  }, [getValues, trigger]);

  const handleContinue = async () => {
    const isFirmDA = watchFirmType === FIRM_TYPE.DA;
    if (!(await trigger())) {
      return;
    }

    await setContextFirm(null);
    await onSubmit();

    isFirmDA ? history.push(registerDueDiligence) : history.push(registerPersonalDetails);
  };

  const handleAddExistingFirm = async (firm: Firm) => {
    await setContextFirm(firm);
    setRegisterFirmForm(getValues());
    history.push(registerPersonalDetails);
  };

  const createAddressOptions = (): Option[] => {
    return addressOptions.map((address: AddressType) => ({
      label: formatAddressLabel(address),
      params: { address },
      name: address.street?.toString(),
      selected: false,
    }));
  };

  const reloadAddressOptions = (postcode: string) => {
    const isPostcodeValid = postcodeValidation().isValidSync(postcode);

    if (postcode && isPostcodeValid) {
      fetchAddresses(postcode).then((addresses) => setAddressOptions(addresses));
    } else {
      setAddressOptions([]);
    }
  };

  const populateAddressFields = (address: AddressType) => {
    setValue(
      ADDRESS_NAMES.ADDRESS_LINE_1,
      [address.flatNumber, address.buildingName].filter(isNotEmptyString).join(' '),
    );
    setValue(ADDRESS_NAMES.ADDRESS_LINE_2, [address.buildingNumber, address.street].filter(isNotEmptyString).join(' '));
    setValue(ADDRESS_NAMES.CITY, address.city);
    address.county ? setValue(ADDRESS_NAMES.COUNTY, address.county) : setValue(ADDRESS_NAMES.COUNTY, '');
    setValue(ADDRESS_NAMES.POSTCODE, address.postcode);
  };

  const watchFirmType = watch(FIELD_NAMES.FIRM_TYPE);
  const watchPostcodeSearch = watch(FIELD_NAMES.SEARCH_FIRM_ADDRESS);

  const shouldDisplayPostcodeWarning = useCallback(
    () => !postcodeValidation().isValidSync(watchPostcodeSearch) && watchPostcodeSearch,
    [watchPostcodeSearch],
  );

  return (
    <BoxWrapper>
      <FormProvider<Form> {...methods}>
        <Heading mode="title" weight="bold">
          Register a new firm
        </Heading>
        <Text color="var(--color-indigo-t11)">
          Your firm doesn't appear to be registered with us yet.
          <br />
          Please add your firm details below.
        </Text>

        <form onSubmit={onSubmit}>
          <Section>
            <FormInputWrapper title={FIELD_LABELS.FIRM_NAME} name={FIELD_NAMES.FIRM_NAME} newUi>
              <TextInput newUi />
            </FormInputWrapper>

            <FormInputWrapper
              newUi
              title={FIELD_LABELS.FCA_NUMBER}
              name={FIELD_NAMES.FCA_NUMBER}
              customErrorMessages={[
                {
                  type: CUSTOM_ERROR.FCA_DUPLICATED_FIRM,
                  render: (error: CustomError) => {
                    const { firm }: { firm?: Firm } = error.params;
                    return (
                      firm && (
                        <InputError>
                          It looks like the firm {firm.name} already exists,{' '}
                          <button type="button" onClick={() => handleAddExistingFirm(firm)}>
                            click here if this is your firm?
                          </button>
                        </InputError>
                      )
                    );
                  },
                },
              ]}>
              <TextInput newUi />
            </FormInputWrapper>

            <FormInputWrapper title={FIELD_LABELS.FIRM_EMAIL} name={FIELD_NAMES.FIRM_EMAIL} newUi>
              <TextInput newUi />
            </FormInputWrapper>

            <FormInputWrapper title={FIELD_LABELS.FIRM_PHONE} name={FIELD_NAMES.FIRM_PHONE} newUi>
              <PhoneInput newUi />
            </FormInputWrapper>
          </Section>

          <HorizontalRuler newUi />

          <Section>
            <Heading mode="heading" weight="bold">
              Firm address
            </Heading>

            <FormInputWrapper
              newUi
              title={FIELD_LABELS.SEARCH_FIRM_ADDRESS}
              name={FIELD_NAMES.SEARCH_FIRM_ADDRESS}
              state={shouldDisplayPostcodeWarning() ? 'warning' : undefined}
              extraContent={
                shouldDisplayPostcodeWarning()
                  ? [
                      {
                        content: 'Please enter a valid postcode',
                        position: 'bottom',
                        variant: 'warning',
                      },
                    ]
                  : []
              }>
              <Search
                newUi
                Component={PostcodeInput}
                onOptionClick={(option) => {
                  populateAddressFields(option.params.address);
                }}
                options={createAddressOptions()}
                onSearchClick={() => watchPostcodeSearch && reloadAddressOptions(watchPostcodeSearch)}
                onSearchInputChange={(value) => reloadAddressOptions(value)}
              />
            </FormInputWrapper>
            <Address newUi />
          </Section>

          <HorizontalRuler newUi />

          <Section>
            <Heading mode="heading" weight="bold" style={{ marginBottom: '24px' }}>
              Firm authorisation
            </Heading>
            <Text>Depending on your authorisation, we may require you to be part of a network</Text>

            <FormInputWrapper title="" name={FIELD_NAMES.FIRM_TYPE} newUi>
              <RadioGroup
                newUi
                name={FIELD_NAMES.FIRM_TYPE}
                options={[
                  {
                    value: FIRM_TYPE.DA,
                    text: (
                      <>
                        My firm is <b>directly authorised</b>
                      </>
                    ),
                  },
                  {
                    value: FIRM_TYPE.AR,
                    text: (
                      <>
                        My firm is an <b>appointed representative</b> (of a network)
                      </>
                    ),
                  },
                ]}
              />
            </FormInputWrapper>
          </Section>

          <Section disabled={!watchFirmType}>
            {!watchFirmType && <FindDefault name="" title="Select a network/club" />}

            {watchFirmType === FIRM_TYPE.AR && (
              <FindNetwork
                title="Find a network"
                name={FIELD_NAMES.NETWORK}
                networks={networks}
                loadNetworksByNetworkName={loadNetworksByNetworkName}
              />
            )}

            {watchFirmType === FIRM_TYPE.DA && (
              <FindClub
                title="Find a club"
                name={FIELD_NAMES.CLUB}
                clubs={clubs}
                loadClubsByClubName={loadClubsByClubName}
              />
            )}
          </Section>

          <HorizontalRuler newUi />

          <Section>
            <FormInputWrapper name={FIELD_NAMES.HAS_PERMISSION_TO_REGISTER_FIRM} title="" newUi>
              <Checkbox newUi title="I have permission to register my firm with LiveMore" />
            </FormInputWrapper>
          </Section>

          <Navigation onBack={handleBack} onContinue={handleContinue} continueDisabled={false} />
        </form>
      </FormProvider>
    </BoxWrapper>
  );
};

export default RegisterFirmStep;
